import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { api } from './lib/api.ts';
import { loadSession, type Session } from './lib/session.ts';
import { RealtimeProvider } from './lib/realtime.tsx';
import { AuthProvider } from './lib/auth.tsx';
import { SiteGate } from './lib/gate.tsx';
import { Login } from './pages/Login.tsx';
import { Home } from './pages/Home.tsx';
import { EventDetail } from './pages/EventDetail.tsx';
import { NewEvent } from './pages/NewEvent.tsx';
import { EventSettings } from './pages/EventSettings.tsx';
import { Watch } from './pages/Watch.tsx';
import { Broadcast } from './pages/Broadcast.tsx';
import { Director } from './pages/Director.tsx';
import { Render } from './pages/Render.tsx';
import { LegalPage } from './pages/Legal.tsx';

/**
 * Laedt das Event, holt die gespeicherte Session und haengt die
 * Realtime-Verbindung darum. Ohne Session geht es zurueck zum Beitritt.
 */
function EventShell({ children, requireOrganizer }: { children: ReactNode; requireOrganizer?: boolean }) {
  const { slug = '' } = useParams();
  const [eventId, setEventId] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [session] = useState<Session | null>(() => loadSession(slug));

  useEffect(() => {
    api
      .getEvent(slug)
      .then((r) => setEventId(r.event.id))
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <Navigate to="/" replace />;
  if (!session || (requireOrganizer && session.role !== 'organizer')) {
    return <Navigate to={`/events/${slug}`} replace />;
  }
  if (!eventId) {
    return (
      <div className="stage-page">
        <div className="stage-bar">
          <span className="stage-bar__title">Verbinde mit dem Event …</span>
        </div>
      </div>
    );
  }

  return (
    <RealtimeProvider eventId={eventId} session={session}>
      {children}
    </RealtimeProvider>
  );
}

/**
 * Die Renderseite fuer den RTMP-Ausgang hat keine gespeicherte Session -- sie
 * wird vom Server selbst in einem unsichtbaren Browser geoeffnet. Sie tritt
 * deshalb mit einer festen Zuschauer-Session bei; senden darf sie nichts, und
 * ihr Name macht in Presence und Chat erkennbar, wer da mitschaut.
 */
function RenderShell() {
  const { slug = '' } = useParams();
  const [eventId, setEventId] = useState<string | null>(null);
  const session = useMemo<Session>(
    () => ({ role: 'spectator', displayName: 'Aufnahme (Server)' }),
    [],
  );

  useEffect(() => {
    api
      .getEvent(slug)
      .then((r) => setEventId(r.event.id))
      .catch(() => undefined);
  }, [slug]);

  if (!eventId) return <div className="render-stage" />;
  return (
    <RealtimeProvider eventId={eventId} session={session}>
      <Render />
    </RealtimeProvider>
  );
}

export function App() {
  return (
    <SiteGate>
    <BrowserRouter>
      <AuthProvider>
      <Routes>
        <Route path="/anmelden" element={<Login mode="login" />} />
        <Route path="/registrieren" element={<Login mode="register" />} />
        <Route path="/" element={<Home />} />
        <Route path="/impressum" element={<LegalPage kind="impressum" />} />
        <Route path="/datenschutz" element={<LegalPage kind="datenschutz" />} />
        <Route path="/events/new" element={<NewEvent />} />
        <Route path="/events/:slug" element={<EventDetail />} />
        <Route path="/events/:slug/einstellungen" element={<EventSettings />} />
        <Route
          path="/events/:slug/live"
          element={
            <EventShell>
              <Watch />
            </EventShell>
          }
        />
        <Route
          path="/events/:slug/senden"
          element={
            <EventShell>
              <Broadcast />
            </EventShell>
          }
        />
        <Route
          path="/events/:slug/regie"
          element={
            <EventShell requireOrganizer>
              <Director />
            </EventShell>
          }
        />
        {/* Nur der Server ruft das auf -- siehe RenderShell. */}
        <Route path="/events/:slug/render" element={<RenderShell />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AuthProvider>
    </BrowserRouter>
    </SiteGate>
  );
}
